import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { dictionary, localizePath } from "@/i18n/translations";
import { api, buildAuthHeaders, resolveImageUrl } from "@/lib/api";
import { setDocumentMeta } from "@/lib/seo";
import { normalizeTextSearch } from "@/pages/dashboard/dashboardUtils";

const ITEMS_PER_PAGE = 12;

export default function DashboardPropertiesPage({ lang }) {
  const copy = dictionary[lang];
  const { session } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);

  const filters = useMemo(
    () => ({
      q: searchParams.get("q") || "",
      listing: searchParams.get("listing") || "all",
      availability: searchParams.get("availability") || "all",
      visibility: searchParams.get("visibility") || "all",
      subArea: searchParams.get("subArea") || "all",
      page: Math.max(1, Number(searchParams.get("page") || "1")),
    }),
    [searchParams],
  );

  useEffect(() => {
    setDocumentMeta({
      lang,
      title: `${copy.dashboardNav.properties} | ${copy.nav.brand}`,
      description: copy.dashboardProperties.subtitle,
    });
  }, [copy.dashboardNav.properties, copy.dashboardProperties.subtitle, copy.nav.brand, lang]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/properties", {
        headers: buildAuthHeaders(session),
      });
      setProperties(response.data || []);
    } catch {
      toast.error(copy.common.requestFailed);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const updateQueryParams = (updates) => {
    const next = new URLSearchParams(searchParams.toString());
    Object.entries(updates).forEach(([key, value]) => {
      const normalized = String(value || "").trim();
      if (!normalized || normalized === "all") {
        next.delete(key);
      } else {
        next.set(key, normalized);
      }
    });

    if (!updates.page) {
      next.delete("page");
    }

    setSearchParams(next);
  };

  const filteredProperties = useMemo(() => {
    const textNeedle = normalizeTextSearch(filters.q);
    return properties.filter((item) => {
      const titleAr = normalizeTextSearch(item.title?.ar || "");
      const titleEn = normalizeTextSearch(item.title?.en || "");
      const locationAr = normalizeTextSearch(item.location?.ar || "");
      const locationEn = normalizeTextSearch(item.location?.en || "");

      const textMatch =
        !textNeedle ||
        [titleAr, titleEn, locationAr, locationEn].some((textValue) =>
          textValue.includes(textNeedle),
        );
      const listingMatch = filters.listing === "all" || item.listing_type === filters.listing;
      const availabilityMatch =
        filters.availability === "all" || item.availability === filters.availability;
      const visibilityMatch =
        filters.visibility === "all" ||
        (filters.visibility === "visible" ? item.is_visible : !item.is_visible);
      const subAreaMatch = filters.subArea === "all" || item.sub_area_code === filters.subArea;

      return textMatch && listingMatch && availabilityMatch && visibilityMatch && subAreaMatch;
    });
  }, [filters.availability, filters.listing, filters.q, filters.subArea, filters.visibility, properties]);

  const totalPages = Math.max(1, Math.ceil(filteredProperties.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(filters.page, totalPages);

  const paginatedProperties = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProperties.slice(start, start + ITEMS_PER_PAGE);
  }, [currentPage, filteredProperties]);

  const onDelete = async (propertyId) => {
    if (!window.confirm(copy.dashboardProperties.confirmDelete)) {
      return;
    }

    try {
      await api.delete(`/admin/properties/${propertyId}`, {
        headers: buildAuthHeaders(session),
      });
      toast.success(copy.common.deleteSuccess);
      await fetchProperties();
    } catch (error) {
      toast.error(error?.response?.data?.detail || copy.common.requestFailed);
    }
  };

  const onEdit = (propertyId) => {
    const returnQuery = searchParams.toString();
    const target = localizePath(lang, `/dashboard/properties/${propertyId}/edit`);
    navigate(returnQuery ? `${target}?return=${encodeURIComponent(returnQuery)}` : target);
  };

  return (
    <div className="space-y-6" data-testid="dashboard-properties-page">
      <section className="space-y-2" data-testid="dashboard-properties-header">
        <h1 className="text-3xl font-semibold text-stone-900 sm:text-4xl" data-testid="dashboard-properties-title">
          {copy.dashboardNav.properties}
        </h1>
        <p className="text-sm text-stone-600" data-testid="dashboard-properties-subtitle">
          {copy.dashboardProperties.subtitle}
        </p>
      </section>

      <Card className="border-gold-500/20 bg-white/90" data-testid="dashboard-properties-filters-card">
        <CardHeader>
          <CardTitle data-testid="dashboard-properties-filters-title">{copy.dashboardProperties.filtersTitle}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-6" data-testid="dashboard-properties-filters-grid">
          <Input
            value={filters.q}
            onChange={(event) => updateQueryParams({ q: event.target.value })}
            placeholder={copy.dashboardProperties.searchPlaceholder}
            data-testid="dashboard-properties-search-input"
            className="xl:col-span-2"
          />

          <Select value={filters.listing} onValueChange={(value) => updateQueryParams({ listing: value })}>
            <SelectTrigger data-testid="dashboard-properties-filter-listing-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="dashboard-properties-filter-listing-all">{copy.dashboardProperties.all}</SelectItem>
              <SelectItem value="sale" data-testid="dashboard-properties-filter-listing-sale">{copy.properties.sale}</SelectItem>
              <SelectItem value="rent" data-testid="dashboard-properties-filter-listing-rent">{copy.properties.rent}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.availability} onValueChange={(value) => updateQueryParams({ availability: value })}>
            <SelectTrigger data-testid="dashboard-properties-filter-availability-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="dashboard-properties-filter-availability-all">{copy.dashboardProperties.all}</SelectItem>
              <SelectItem value="available" data-testid="dashboard-properties-filter-availability-available">{copy.properties.available}</SelectItem>
              <SelectItem value="sold" data-testid="dashboard-properties-filter-availability-sold">{copy.properties.sold}</SelectItem>
              <SelectItem value="rented" data-testid="dashboard-properties-filter-availability-rented">{copy.properties.rented}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.visibility} onValueChange={(value) => updateQueryParams({ visibility: value })}>
            <SelectTrigger data-testid="dashboard-properties-filter-visibility-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="dashboard-properties-filter-visibility-all">{copy.dashboardProperties.all}</SelectItem>
              <SelectItem value="visible" data-testid="dashboard-properties-filter-visibility-visible">{copy.dashboard.visible}</SelectItem>
              <SelectItem value="hidden" data-testid="dashboard-properties-filter-visibility-hidden">{copy.dashboard.hidden}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.subArea} onValueChange={(value) => updateQueryParams({ subArea: value })}>
            <SelectTrigger data-testid="dashboard-properties-filter-subarea-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="dashboard-properties-filter-subarea-all">{copy.dashboardProperties.all}</SelectItem>
              {Object.entries(copy.subAreas).map(([key, label]) => (
                <SelectItem value={key} key={key} data-testid={`dashboard-properties-filter-subarea-${key}`}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={() => setSearchParams(new URLSearchParams())} data-testid="dashboard-properties-reset-filters-button">
            {copy.dashboardProperties.resetFilters}
          </Button>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between" data-testid="dashboard-properties-toolbar">
        <p className="text-sm text-stone-600" data-testid="dashboard-properties-result-count">
          {filteredProperties.length}
        </p>
        <Button
          onClick={() => navigate(localizePath(lang, "/dashboard/properties/new"))}
          data-testid="dashboard-properties-add-new-button"
        >
          {copy.dashboardNav.addProperty}
        </Button>
      </div>

      <section className="grid gap-4 lg:grid-cols-2" data-testid="dashboard-properties-list-grid">
        {loading && <p data-testid="dashboard-properties-loading">{copy.common.loading}</p>}

        {!loading && filteredProperties.length === 0 && (
          <p className="text-sm text-stone-600" data-testid="dashboard-properties-empty-state">
            {copy.dashboardProperties.noResults}
          </p>
        )}

        {!loading &&
          paginatedProperties.map((property) => (
            <Card key={property.id} className="border-gold-500/20 bg-white/95" data-testid={`dashboard-properties-card-${property.id}`}>
              <CardContent className="space-y-4 p-4">
                <div className="flex items-center justify-between gap-3" data-testid={`dashboard-properties-card-head-${property.id}`}>
                  <div>
                    <p className="text-lg font-semibold text-stone-900" data-testid={`dashboard-properties-card-title-${property.id}`}>
                      {lang === "ar" ? property.title?.ar : property.title?.en}
                    </p>
                    <p className="text-xs text-stone-600" data-testid={`dashboard-properties-card-location-${property.id}`}>
                      {lang === "ar" ? property.location?.ar : property.location?.en}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1" data-testid={`dashboard-properties-card-badges-${property.id}`}>
                    <Badge data-testid={`dashboard-properties-card-listing-${property.id}`}>{copy.properties[property.listing_type]}</Badge>
                    <Badge variant="outline" data-testid={`dashboard-properties-card-availability-${property.id}`}>{copy.properties[property.availability]}</Badge>
                    <Badge variant="outline" data-testid={`dashboard-properties-card-subarea-${property.id}`}>{copy.subAreas[property.sub_area_code] || property.sub_area_code}</Badge>
                  </div>
                </div>

                {property.images?.[0] && (
                  <img
                    src={resolveImageUrl(property.images[0])}
                    alt={lang === "ar" ? property.title?.ar : property.title?.en}
                    className="h-40 w-full rounded-xl object-cover"
                    data-testid={`dashboard-properties-card-image-${property.id}`}
                  />
                )}

                <div className="flex flex-wrap items-center justify-between gap-2" data-testid={`dashboard-properties-card-footer-${property.id}`}>
                  <p className="text-sm font-semibold text-gold-900" data-testid={`dashboard-properties-card-price-${property.id}`}>
                    {property.price} {property.currency}
                  </p>
                  <div className="flex gap-2" data-testid={`dashboard-properties-card-actions-${property.id}`}>
                    <Button variant="outline" onClick={() => onEdit(property.id)} data-testid={`dashboard-properties-card-edit-${property.id}`}>
                      {copy.dashboard.edit}
                    </Button>
                    <Button variant="destructive" onClick={() => onDelete(property.id)} data-testid={`dashboard-properties-card-delete-${property.id}`}>
                      {copy.dashboard.delete}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
      </section>

      <div className="flex items-center justify-between rounded-2xl border border-gold-500/20 bg-white/80 px-4 py-3" data-testid="dashboard-properties-pagination-bar">
        <Button
          variant="outline"
          disabled={currentPage <= 1}
          onClick={() => updateQueryParams({ page: String(currentPage - 1) })}
          data-testid="dashboard-properties-pagination-prev-button"
        >
          {copy.dashboardProperties.previous}
        </Button>

        <span className="text-sm text-stone-700" data-testid="dashboard-properties-pagination-info">
          {currentPage} / {totalPages}
        </span>

        <Button
          variant="outline"
          disabled={currentPage >= totalPages}
          onClick={() => updateQueryParams({ page: String(currentPage + 1) })}
          data-testid="dashboard-properties-pagination-next-button"
        >
          {copy.dashboardProperties.next}
        </Button>
      </div>
    </div>
  );
}
